"use client";

import { type VariantProps, cva } from "class-variance-authority";
import { CheckIcon, XCircle } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
	"m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
	{
		variants: {
			variant: {
				default:
					"border-foreground/10 text-foreground bg-card hover:bg-card/80",
				secondary:
					"border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
				destructive:
					"border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
				inverted: "inverted",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
	VariantProps<typeof multiSelectVariants> {
	/**
	 * An array of option objects to be displayed in the multi-select component.
	 * Each option object has a label, value, and an optional icon.
	 */
	options: {
		/** The text to display for the option. */
		label: string;
		/** The unique value associated with the option. */
		value: string;
		/** Optional icon component to display alongside the option. */
		icon?: React.ComponentType<{ className?: string }>;
	}[];

	/**
	 * Callback function triggered when the selected values change.
	 * Receives an array of the new selected values.
	 */
	onValueChange: (value: string[]) => void;

	/** Los valores seleccionados actualmente (hace que el componente sea controlado). */
	value: string[]; // <-- Renombrado y tipo definido

	/**
	 * Placeholder text to be displayed when no values are selected.
	 * Optional, defaults to "Select options".
	 */
	placeholder?: string;

	/**
	 * Animation duration in seconds for the visual effects (e.g., bouncing badges).
	 * Optional, defaults to 0 (no animation).
	 */
	animation?: number;

	/**
	 * Maximum number of items to display. Extra selected items will be summarized.
	 * Optional, defaults to 3.
	 */
	maxCount?: number;

	/**
	 * The modality of the popover. When set to true, interaction with outside elements
	 * will be disabled and only popover content will be visible to screen readers.
	 * Optional, defaults to false.
	 */
	modalPopover?: boolean;

	/**
	 * If true, renders the multi-select component as a child of another component.
	 * Optional, defaults to false.
	 */
	asChild?: boolean;

	/**
	 * Additional class names to apply custom styles to the multi-select component.
	 * Optional, can be used to add custom styles.
	 */
	className?: string;
}

export const MultiSelect = React.forwardRef<
	HTMLButtonElement,
	MultiSelectProps
>(
	(
		{
			options,
			onValueChange,
			variant,
			value = [],
			placeholder = "Selecciona opciones",
			animation = 0,
			maxCount = 3,
			modalPopover = false,
			asChild = false,
			className,
			...props
		},
		ref,
	) => {
		// Estado interno inicializado con el valor de la prop
		const [selectedValues, setSelectedValues] =
			React.useState<string[]>(value);
		const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

		// SOLUCIÓN: Sincronizar el estado interno con la prop 'value'
		React.useEffect(() => {
			if (value.length !== selectedValues.length || !value.every((v, i) => v === selectedValues[i])) {
				setSelectedValues(value);
			}
		}, [value, selectedValues]); // Incluye selectedValues para cumplir con Biome/ESLint

		const handleInputKeyDown = (
			event: React.KeyboardEvent<HTMLInputElement>,
		) => {
			if (event.key === "Enter") {
				setIsPopoverOpen(true);
			} else if (event.key === "Backspace" && !event.currentTarget.value) {
				const newSelectedValues = [...selectedValues];
				newSelectedValues.pop();
				setSelectedValues(newSelectedValues);
				onValueChange(newSelectedValues);
			}
		};

		const toggleOption = (option: string) => {
			const newSelectedValues = selectedValues.includes(option)
				? selectedValues.filter((v) => v !== option)
				: [...selectedValues, option];
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues); // Notifica al componente padre (DataTableFilterList)
		};

		const handleClear = () => {
			setSelectedValues([]);
			onValueChange([]);
		};

		const handleTogglePopover = () => {
			setIsPopoverOpen((prev) => !prev);
		};

		const clearExtraOptions = () => {
			const newSelectedValues = selectedValues.slice(0, maxCount);
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues);
		};

		const toggleAll = () => {
			if (selectedValues.length === options.length) {
				handleClear();
			} else {
				const allValues = options?.map((option) => option.value);
				setSelectedValues(allValues);
				onValueChange(allValues);
			}
		};

		return (
			<Popover
				open={isPopoverOpen}
				onOpenChange={setIsPopoverOpen}
				modal={modalPopover}
			>
				<PopoverTrigger asChild>
					<Button
						ref={ref}
						{...props}
						type="button"
						onClick={handleTogglePopover}
						className={cn(
							"flex h-auto min-h-10 w-full items-center justify-between rounded-md border bg-inherit p-1 hover:bg-inherit",
							className,
						)}
					>
						{selectedValues.length > 0 ? (
							<div className="flex w-full items-center justify-between pl-3.5">
								<div className="flex flex-nowrap items-center">
									{selectedValues.slice(0, maxCount).map((selectedValue) => { // <-- USAR selectedValue
										const option = options.find((o) => o.value === selectedValue); // <-- USAR selectedValue
										const IconComponent = option?.icon;
										return (
											<span
												key={selectedValue}
												className={cn(
													"mr-2 flex items-center font-normal text-black",
												)}
												style={{ animationDuration: `${animation}s` }}
											>
												{IconComponent && (
													<IconComponent className="mr-2 h-4 w-4" />
												)}
												{option?.label}
												<span
													// biome-ignore lint/a11y/useSemanticElements: <explanation>
													role="button"
													aria-label={`Eliminar ${option?.label}`}
													tabIndex={0}
													className="ml-2 flex cursor-pointer items-center outline-none"
													onClick={(event) => {
														event.stopPropagation();
														event.preventDefault();
														toggleOption(selectedValue); // <-- USAR selectedValue
													}}
													onKeyDown={(event) => {
														if (event.key === "Enter" || event.key === " ") {
															event.stopPropagation();
															event.preventDefault();
															toggleOption(selectedValue); // <-- USAR selectedValue
														}
													}}
													onMouseDown={(event) => {
														event.stopPropagation();
														event.preventDefault();
													}}
												>
													<XCircle className="h-4 w-4 transition-colors hover:text-destructive" />
												</span>
											</span>
										);
									})}
									{selectedValues.length > maxCount && (
										<div
											className={cn(
												"ml-2 flex items-center font-normal text-black",
											)}
										>
											{`+ ${selectedValues.length - maxCount}`}
											<XCircle
												className="ml-2 h-4 w-4 cursor-pointer"
												onMouseDown={(event) => {
													event.preventDefault();
													event.stopPropagation();
												}}
												onClick={(event) => {
													event.preventDefault();
													event.stopPropagation();
													clearExtraOptions();
												}}
											/>
										</div>
									)}
								</div>
							</div>
						) : (
							<div className="mx-auto flex w-full items-center justify-between">
								<span className="mx-3 text-black text-sm">{placeholder}</span>
							</div>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="w-auto p-0"
					align="start"
					onEscapeKeyDown={() => setIsPopoverOpen(false)}
				>
					<Command>
						<CommandInput
							placeholder="Buscar..."
							onKeyDown={handleInputKeyDown}
						/>
						<CommandList>
							<CommandEmpty>Sin Resultados.</CommandEmpty>
							<CommandGroup>
								<CommandItem
									key="all"
									onSelect={toggleAll}
									className="cursor-pointer"
								>
									<div
										className={cn(
											"mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
											selectedValues.length === options.length
												? "bg-primary text-primary-foreground"
												: "opacity-50 [&_svg]:invisible",
										)}
									>
										<CheckIcon className="h-4 w-4" />
									</div>
									<span>(Seleccionar todo)</span>
								</CommandItem>
								{options.map((option) => {
									const isSelected = selectedValues.includes(option.value);
									return (
										<CommandItem
											key={option.value}
											onSelect={() => toggleOption(option.value)}
											className="cursor-pointer"
											disabled={false}
										>
											<div
												className={cn(
													"mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
													isSelected
														? "bg-primary text-primary-foreground"
														: "opacity-50 [&_svg]:invisible",
												)}
											>
												<CheckIcon className="h-4 w-4" />
											</div>
											{option.icon && (
												<option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
											)}
											<span>{option.label}</span>
										</CommandItem>
									);
								})}
							</CommandGroup>
							<CommandSeparator />
							<CommandGroup>
								<div className="flex items-center justify-between">
									{selectedValues.length > 0 && (
										<>
											<CommandItem
												onSelect={handleClear}
												className="flex-1 cursor-pointer justify-center"
											>
												Limpiar
											</CommandItem>
											<Separator
												orientation="vertical"
												className="flex h-full min-h-6"
											/>
										</>
									)}
									<CommandItem
										onSelect={() => setIsPopoverOpen(false)}
										className="max-w-full flex-1 cursor-pointer justify-center"
									>
										Cerrar
									</CommandItem>
								</div>
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		);
	},
);

MultiSelect.displayName = "MultiSelect";